import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, getAllRoles } from '@/stores/auth.store';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import VaegGuide from '@/components/shared/VaegGuide';
import { api } from '@/lib/api';

function VaegGuidePortal() {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated || !user) return null;
  return <VaegGuide />;
}

// Layouts (eager — needed immediately on any authenticated route)
import ClientLayout from '@/components/layout/ClientLayout';
import JuristeLayout from '@/components/layout/JuristeLayout';
import AdminLayout from '@/components/layout/AdminLayout';
import ValidateurLayout from '@/components/layout/ValidateurLayout';

// Auth pages (eager — public, tiny)
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import ChangePasswordPage from '@/pages/auth/ChangePasswordPage';

// ── Lazy pages ────────────────────────────────────────────────────────────────
const ClientDashboard    = lazy(() => import('@/pages/client/Dashboard'));
const ClientMissions     = lazy(() => import('@/pages/client/Missions'));
const ClientMissionNew   = lazy(() => import('@/pages/client/MissionNew'));
const ClientMissionDetail= lazy(() => import('@/pages/client/MissionDetail'));
const ClientDocuments    = lazy(() => import('@/pages/client/Documents'));
const ClientSubscription = lazy(() => import('@/pages/client/Subscription'));
const ClientQuoteDetail  = lazy(() => import('@/pages/client/QuoteDetail'));
const ClientProfile      = lazy(() => import('@/pages/client/Profile'));
const ClientMessages     = lazy(() => import('@/pages/client/Messages'));
const ClientTeam         = lazy(() => import('@/pages/client/Team'));
const ClientSignatures   = lazy(() => import('@/pages/client/Signatures'));
const ClientLexWork      = lazy(() => import('@/pages/client/LexWork'));
const ClientDrive        = lazy(() => import('@/pages/client/Drive'));
const ClientAide         = lazy(() => import('@/pages/client/Aide'));
const ClientTemplates        = lazy(() => import('@/pages/client/Templates'));
const ClientCreationSociete  = lazy(() => import('@/pages/client/CreationSociete'));

const JuristeDashboard     = lazy(() => import('@/pages/juriste/Dashboard'));
const JuristeMissions      = lazy(() => import('@/pages/juriste/Missions'));
const JuristeMissionDetail = lazy(() => import('@/pages/juriste/MissionDetail'));
const JuristeTemplates     = lazy(() => import('@/pages/juriste/Templates'));
const JuristeTime          = lazy(() => import('@/pages/juriste/Time'));
const JuristeSignatures    = lazy(() => import('@/pages/juriste/Signatures'));
const JuristeLexBase       = lazy(() => import('@/pages/juriste/LexBase'));
const JuristeLexWork       = lazy(() => import('@/pages/juriste/LexWork'));
const JuristeDrive         = lazy(() => import('@/pages/juriste/Drive'));
const JuristeAide          = lazy(() => import('@/pages/juriste/Aide'));

const AdminDashboard      = lazy(() => import('@/pages/admin/Dashboard'));
const AdminMissions       = lazy(() => import('@/pages/admin/Missions'));
const AdminMissionDetail  = lazy(() => import('@/pages/admin/MissionDetail'));
const AdminClients     = lazy(() => import('@/pages/admin/Clients'));
const AdminClientDetail= lazy(() => import('@/pages/admin/ClientDetail'));
const AdminJuristes    = lazy(() => import('@/pages/admin/Juristes'));
const AdminQuotes      = lazy(() => import('@/pages/admin/Quotes'));
const AdminInvoices    = lazy(() => import('@/pages/admin/Invoices'));
const AdminUsers       = lazy(() => import('@/pages/admin/Users'));
const AdminAuditLog    = lazy(() => import('@/pages/admin/AuditLog'));
const AdminAnomalies   = lazy(() => import('@/pages/admin/Anomalies'));
const AdminLexBase       = lazy(() => import('@/pages/admin/LexBase'));
const AdminSignatures    = lazy(() => import('@/pages/admin/Signatures'));
const AdminFeatureFlags  = lazy(() => import('@/pages/admin/FeatureFlags'));
const AdminLoginHistory  = lazy(() => import('@/pages/admin/LoginHistory'));

const ValidateurDashboard = lazy(() => import('@/pages/validateur/Dashboard'));

const NotificationsPage = lazy(() => import('@/pages/shared/Notifications'));
const CalendarPage      = lazy(() => import('@/pages/shared/Calendar'));
const SocieteCreationOverview = lazy(() => import('@/pages/shared/SocieteCreationOverview'));

// ── Page-level suspense fallback ──────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 300 }}>
      <div style={{ width: 28, height: 28, border: '3px solid #B8960A', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function useHydrated() {
  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated());
  useEffect(() => {
    if (hydrated) return;
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    setHydrated(useAuthStore.persist.hasHydrated());
    return unsub;
  }, [hydrated]);
  return hydrated;
}

const ROLE_DASHBOARD: Record<string, string> = {
  SUPER_ADMIN: '/app/admin/dashboard',
  ADMIN:       '/app/admin/dashboard',
  COMMERCIAL:  '/app/admin/dashboard',
  OPERATIONS:  '/app/admin/dashboard',
  JURISTE:     '/app/juriste/dashboard',
  VALIDATEUR:  '/app/validateur/dashboard',
  CLIENT:      '/app/client/dashboard',
};

function ProtectedRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { isAuthenticated, user, activeRole } = useAuthStore();
  const hydrated = useHydrated();

  if (!hydrated) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100dvh', background: '#FEF9EC' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #B8960A', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.must_change_password) return <Navigate to="/change-password" replace />;
  // Vérifie si l'une des routes autorisées est dans les rôles de l'utilisateur (primaire + additionnels)
  if (roles && user && !roles.some(r => getAllRoles(user).includes(r as never))) {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}

function AppRedirect() {
  const { user, activeRole } = useAuthStore();
  if (!user) return <Navigate to="/login" />;
  const target = activeRole ? ROLE_DASHBOARD[activeRole] : ROLE_DASHBOARD[user.role];
  return <Navigate to={target ?? '/login'} />;
}

export default function App() {
  useEffect(() => {
    const report = (title: string, message: string, stack?: string, context?: unknown) => {
      void api.post('/anomalies/report', {
        source: 'CLIENT',
        severity: 'ERROR',
        title,
        message,
        stack,
        route: window.location.pathname,
        context,
      }).catch(() => {});
    };

    const onError = (event: ErrorEvent) => {
      report('Erreur JavaScript client', event.message, event.error?.stack, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      report(
        'Promesse rejetee non geree',
        reason instanceof Error ? reason.message : String(reason),
        reason instanceof Error ? reason.stack : undefined,
      );
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />

          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="/app" element={<ProtectedRoute><AppRedirect /></ProtectedRoute>} />

          {/* CLIENT */}
          <Route path="/app/client" element={<ProtectedRoute roles={['CLIENT']}><ClientLayout /></ProtectedRoute>}>
            <Route path="dashboard"       element={<ClientDashboard />} />
            <Route path="missions"        element={<ClientMissions />} />
            <Route path="missions/new"    element={<ClientMissionNew />} />
            <Route path="missions/:id"    element={<ClientMissionDetail />} />
            <Route path="messages"        element={<ClientMessages />} />
            <Route path="team"            element={<ClientTeam />} />
            <Route path="documents"       element={<ClientDocuments />} />
            <Route path="quotes/:id"      element={<ClientQuoteDetail />} />
            <Route path="signatures"      element={<ClientSignatures />} />
            <Route path="subscription"    element={<ClientSubscription />} />
            <Route path="profile"         element={<ClientProfile />} />
            <Route path="lexcreate"          element={<Navigate to="/app/client/creation-societe" replace />} />
            <Route path="lexwork"            element={<ClientLexWork />} />
            <Route path="drive"              element={<ClientDrive />} />
            <Route path="templates"          element={<ClientTemplates />} />
            <Route path="creation-societe"   element={<ClientCreationSociete />} />
            <Route path="calendar"           element={<CalendarPage />} />
            <Route path="aide"               element={<ClientAide />} />
            <Route path="notifications"      element={<NotificationsPage />} />
          </Route>

          {/* JURISTE */}
          <Route path="/app/juriste" element={<ProtectedRoute roles={['JURISTE']}><JuristeLayout /></ProtectedRoute>}>
            <Route path="dashboard"       element={<JuristeDashboard />} />
            <Route path="missions"        element={<JuristeMissions />} />
            <Route path="missions/:id"    element={<JuristeMissionDetail />} />
            <Route path="templates"       element={<JuristeTemplates />} />
            <Route path="time"            element={<JuristeTime />} />
            <Route path="lexbase"         element={<JuristeLexBase />} />
            <Route path="lexwork"         element={<JuristeLexWork />} />
            <Route path="drive"           element={<JuristeDrive />} />
            <Route path="aide"            element={<JuristeAide />} />
            <Route path="signatures"      element={<JuristeSignatures />} />
            <Route path="lexcreate"       element={<SocieteCreationOverview />} />
            <Route path="calendar"        element={<CalendarPage />} />
            <Route path="notifications"   element={<NotificationsPage />} />
          </Route>

          {/* VALIDATEUR */}
          <Route path="/app/validateur" element={<ProtectedRoute roles={['VALIDATEUR', 'SUPER_ADMIN', 'ADMIN']}><ValidateurLayout /></ProtectedRoute>}>
            <Route path="dashboard"     element={<ValidateurDashboard />} />
            <Route path="queue"         element={<ValidateurDashboard />} />
            <Route path="lexbase"       element={<ValidateurDashboard />} />
            <Route path="notifications" element={<NotificationsPage />} />
          </Route>

          {/* ADMIN */}
          <Route path="/app/admin" element={<ProtectedRoute roles={['SUPER_ADMIN', 'ADMIN', 'COMMERCIAL', 'OPERATIONS']}><AdminLayout /></ProtectedRoute>}>
            <Route path="dashboard"      element={<AdminDashboard />} />
            <Route path="missions"       element={<AdminMissions />} />
            <Route path="missions/:id"   element={<AdminMissionDetail />} />
            <Route path="clients"       element={<AdminClients />} />
            <Route path="clients/:id"   element={<AdminClientDetail />} />
            <Route path="juristes"      element={<AdminJuristes />} />
            <Route path="quotes"        element={<AdminQuotes />} />
            <Route path="invoices"      element={<AdminInvoices />} />
            <Route path="users"         element={<AdminUsers />} />
            <Route path="audit"         element={<AdminAuditLog />} />
            <Route path="anomalies"     element={<AdminAnomalies />} />
            <Route path="lexbase"       element={<AdminLexBase />} />
            <Route path="signatures"    element={<AdminSignatures />} />
            <Route path="feature-flags" element={<AdminFeatureFlags />} />
            <Route path="login-history" element={<AdminLoginHistory />} />
            <Route path="lexcreate"     element={<SocieteCreationOverview />} />
            <Route path="calendar"      element={<CalendarPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <VaegGuidePortal />
    </ErrorBoundary>
  );
}
